import { UnreachableCaseError } from 'ts-essentials';
import {
  assertValidGeovectorRegionAndEnvironment,
  type GeovectorClientEnvironment,
  type GeovectorRegion,
} from '@trimble/utilities';
import { createClient, type Client } from '../generated/client';
import { getAuthToken } from '../generated/core/auth.gen';

export type ServiceType = 'features' | 'tiles' | 'cloud';

const BEARER_SECURITY = { scheme: 'bearer', type: 'http' } as const;

/**
 * Hey-api only calls `config.auth` for operations with a `security` array, which
 * the Features OpenAPI omits, so `setConfig({ auth })` would otherwise be a no-op.
 */
export async function applyFeaturesServiceAuth(
  request: Request,
  client: Client
): Promise<Request> {
  if (!request.headers.has('Authorization')) {
    const token = await getAuthToken(BEARER_SECURITY, client.getConfig().auth);
    if (token) {
      request.headers.set('Authorization', token);
    }
  }
  return request;
}

export interface FeaturesClientOptions {
  env: GeovectorClientEnvironment;
  region: GeovectorRegion;
  service?: ServiceType;
  baseUrl?: string;
}

const getFeaturesServiceBaseUrl = ({
  env,
  region,
  service = 'features',
}: FeaturesClientOptions): string => {
  assertValidGeovectorRegionAndEnvironment(env, region);

  const envSubdomain = env === 'prod' ? '' : `${env}.`;
  const cloudSubdomain =
    env === 'prod' ? 'api.trimble.com' : 'stage.api.trimblecloud.com';

  switch (service) {
    case 'cloud':
      //TODO: openapi does not exist yet but it will be added soon - https://jira.trimble.tools/browse/GCC-107
      // see docs on https://cloud.stage.api.trimblecloud.com/Features-Service/features-na/1.0/swagger
      return `https://cloud.${cloudSubdomain}/Features-Service/features-${region}/1.0/`;

    case 'features':
    case 'tiles':
      return `https://${region}.${envSubdomain}${service}.maps.trimblegeospatial.com/`;

    default:
      throw new UnreachableCaseError(service);
  }
};

export const createFeaturesServiceClient = ({
  env,
  region,
  service = 'features',
  baseUrl: baseUrlOverride,
}: FeaturesClientOptions): Client => {
  const baseUrl =
    baseUrlOverride ??
    getFeaturesServiceBaseUrl({
      env,
      region,
      service,
    });

  const client = createClient({ baseUrl });
  client.interceptors.request.use((request) =>
    applyFeaturesServiceAuth(request, client)
  );
  return client;
};
